import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { ApiError } from "../lib/api";
import { Brand } from "../components/Brand";
import { GoogleButton } from "../components/GoogleButton";

export default function Register() {
  const { signUp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invite = params.get("invite") || undefined;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password.length > 72) {
      setError("Password must be 72 characters or fewer.");
      return;
    }
    setLoading(true);
    try {
      const result = await signUp(email.trim(), password, name.trim() || undefined, invite);
      if (result.pending) {
        setPending(result.message || "Thanks for signing up. We will be in touch by email shortly.");
      } else {
        nav("/library", { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* LEFT - quiet brand panel */}
      <section className="hidden lg:flex flex-col justify-center bg-paper-deep border-r border-hairline-bold px-12 xl:px-16 py-16">
        <Link to="/" aria-label="DreamBook home" className="self-start mb-10">
          <Brand size="lg" />
        </Link>
        <p className="eyebrow">An engine for full-length books</p>
        <h1 className="mt-3 font-serif text-[clamp(1.9rem,3vw,2.8rem)] leading-[1.08] text-ink-body">
          Draft, critique, and <em className="italic text-terracotta">revise</em> a whole book, then finish it in the editor.
        </h1>
        <p className="mt-4 max-w-[46ch] font-body text-[17px] leading-relaxed text-ink-dim">
          Publish free to the Library, or list it for sale in the Marketplace. Named editors read your draft and leave letters.
        </p>
        <p className="mt-8 font-body text-[17px] text-ink-body">
          <span className="font-medium">Start free. 2,000 credits, no card.</span>
        </p>
        <p className="mt-10 font-hand text-[1.25rem] text-ink-faint">Dreambook into being.</p>
      </section>

      {/* RIGHT - form card */}
      <section className="flex flex-col justify-center items-center px-6 sm:px-10 py-14">
        <div className="w-full max-w-[440px]">
          {/* compact brand for narrow viewports */}
          <Link to="/" aria-label="DreamBook home" className="lg:hidden mx-auto mb-10 block w-fit">
            <Brand size="md" />
          </Link>

          {pending ? (
            <div className="surface p-7 sm:p-8 text-center">
              <div className="eyebrow">Thanks for signing up</div>
              <p className="mt-4 font-body text-[16px] leading-relaxed text-ink-body">{pending}</p>
              <Link to="/login" className="mt-6 inline-block text-terracotta hover:text-gold-300">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="eyebrow">Create your account</div>
              <h2 className="mt-2 font-serif text-[clamp(1.5rem,2.4vw,2rem)] leading-tight text-ink-body">
                Begin your first book
              </h2>
              <p className="mt-2 font-body text-[15px] text-ink-dim">
                Start free. 2,000 credits, no card required.
              </p>

              <div className="surface mt-7 p-7 sm:p-8">
                <form onSubmit={onSubmit}>
                  {invite ? (
                    <div className="mb-5 text-sm text-terracotta">
                      You've been invited. Finish creating your account below.
                    </div>
                  ) : null}

                  <label className="field-label" htmlFor="name">Name (optional)</label>
                  <input
                    id="name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="field mb-1.5"
                    placeholder="How you'd like to be addressed"
                  />
                  <p className="mb-5 font-body text-[12px] text-ink-faint">
                    This is your public byline. Falls back to "Anonymous" if left blank.
                  </p>

                  <label className="field-label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="field mb-5"
                    placeholder="you@example.com"
                  />

                  <label className="field-label" htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="field mb-5"
                    placeholder="At least 8 characters"
                  />

                  <div className="mb-5 flex items-center gap-3 rounded-sm border border-hairline-bold bg-paper-deep px-3.5 py-2.5">
                    <span className="font-mono text-[9px] tracking-smallcaps uppercase px-1.5 py-0.5 rounded-sm bg-sage-soft text-sage-deep">
                      Free
                    </span>
                    <span className="font-body text-[13px] leading-snug text-ink-dim">
                      You'll get <span className="text-ink-body font-medium">2,000 credits</span> to start. Enough to begin your first book.
                    </span>
                  </div>

                  {error ? (
                    <div className="mb-5 text-sm text-wax" role="alert">{error}</div>
                  ) : null}

                  <button type="submit" className="btn-primary w-full" disabled={loading}>
                    {loading ? "Creating…" : "Create account"}
                  </button>

                  <GoogleButton label="signup_with" />
                </form>

                <p className="mt-5 font-body text-[12px] leading-relaxed text-ink-faint">
                  By creating an account you agree to our Terms of Service and Privacy Policy. We'll send a verification link, and you can start building right away.
                </p>

                <p className="mt-5 text-center font-body text-sm text-ink-dim">
                  Already have an account?{" "}
                  <Link to="/login" className="text-terracotta hover:text-gold-300">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
